import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, withFresh, ApiError } from '../api';
import { abbrM, abbrPct, money, nfmt, pctF } from '../format';
import { lbl } from '../labels';
import { Button } from '../components/Button';
import { Card, Cards } from '../components/Card';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';
import { InfoTip } from '../components/InfoTip';
import { ImportDialog } from './ImportDialog';
import { AddTicker } from './AddTicker';
import { Select } from '../components/Select';
import { Menu, useMenu } from '../components/Menu';
import { PanePageSize, PanePager } from '../components/PanePageSize';
import { Table } from '../components/Table';
import type { Cell } from '../components/Table';
import { Tabs } from '../components/Tabs';
import type { Row } from '../components/Table';

/**
 * The list page — the read-only half (P4).
 *
 * Ported: the list switcher, the Show filter (Open / All / Closed), the sort select, the five summary
 * cards, and the holdings table with a row that opens its ticker pane (fetched lazily from
 * `/api/v1/lists/:id/tickers/:ticker`, the endpoint the EJS pane has always used).
 *
 * Not ported yet, and named on the page rather than silently absent: adding and editing transactions,
 * delete, the selection bar, the CSV import dialog and drag reorder — all of them writes, and each has an
 * endpoint already.
 */
type Holding = {
  /** The company/ticker name the row shows, and whether this row is a watchlist entry (no transactions). */
  name?: string;
  watch?: boolean;
  ticker: string;
  shares: number;
  avg_cost: number | null;
  price: number | null;
  value: number;
  unrealized: number | null;
  realized: number | null;
  realizedPct?: number | null;
  dayGain: number | null;
  dayGainPct: number | null;
  totalGain: number | null;
  unpriced: boolean;
  /** The quote session the row's day figures come from (portfolio.js sets dayAsOf). */
  dayAsOf?: string | null;
  daySessionDate?: string | null;
  closed?: boolean;
  txCount?: number;
};

type Stats = {
  totalValue: number;
  totalCost: number;
  unrealized: number;
  dayGain: number;
  dayChangePct: number | null;
  totalGain: number;
  realized: number;
  /** Paid out on this list's tickers — part of the total-gain card's own breakdown, as the API sends it. */
  dividends: number;
  unrealizedPct: number | null;
};

type PriceInfo = {
  fetchedAt: string | null;
  stale: boolean;
  staleCount: number;
  marketOpen: boolean;
  refreshMs?: number;
  refreshed?: number;
  timedOut?: boolean;
  skipped?: string;
  error?: string;
  forced?: boolean;
};

/** The one-line outcome of a refresh, for the column tooltip that already reports the quote date. */
function refreshOutcome(p?: PriceInfo): string {
  if (!p) return '';
  if (p.refreshed) return `${p.refreshMs ?? 0}ms · ${p.refreshed} ticker${p.refreshed === 1 ? '' : 's'}`;
  if (p.skipped === 'backoff') return 'pipeline backoff — no request made';
  if (p.skipped === 'provider') return 'provider refused — cached';
  if (p.skipped === 'error') return `failed${p.error ? ': ' + p.error : ''}`;
  if (p.timedOut) return `timed out at ${p.refreshMs ?? 0}ms`;
  return 'nothing to refresh';
}

type ListPayload = {
  list: { id: number; name: string; type: string; sort_mode?: string; sort_asc?: boolean };
  /** What the server did about price freshness on this load (openspec: market-data). */
  prices?: PriceInfo;
  /** The panes' rows-per-page preference, stored per user (localStorage is per origin). */
  paneSizes?: { transactions: string; dividends: string };
  holdings: Holding[];
  closedTickers: Holding[];
  stats: Stats;
  userLists: { id: number; name: string }[];
  tickerNames?: Record<string, string>;
};

const SORT_OPTIONS = [
  ['custom', 'Sort by: Custom'],
  ['ticker', 'Sort by: Ticker'],
  ['value', 'Sort by: Market Value'],
  ['daygain', "Sort by: Day's Gain"],
  ['daygainpct', "Sort by: Day's Gain (%)"],
  ['totalgain', 'Sort by: Total Gain'],
  ['totalgainpct', 'Sort by: Total Gain (%)'],
] as const;

/**
 * Sort the rows the way views/list.ejs does: ONE ascending comparator per mode, flipped when the direction is
 * descending (`sortAsc ? cmp(a,b) : cmp(b,a)`). The SPA had the comparators but no direction — it sorted
 * descending for money and ascending for ticker, and the ▲/▼ button only flipped its own glyph, so "doesn't do
 * anything" was exactly right.
 */
function sortRows(rows: Holding[], mode: string, asc: boolean): Holding[] {
  const cmp: Record<string, (a: Holding, b: Holding) => number> = {
    ticker: (a, b) => a.ticker.localeCompare(b.ticker),
    value: (a, b) => a.value - b.value,
    daygain: (a, b) => (a.dayGain ?? 0) - (b.dayGain ?? 0),
    daygainpct: (a, b) => (a.dayGainPct ?? 0) - (b.dayGainPct ?? 0),
    totalgain: (a, b) => (a.totalGain ?? 0) - (b.totalGain ?? 0),
    totalgainpct: (a, b) => (a.totalGain ?? 0) / (Math.abs(a.value) || 1) - (b.totalGain ?? 0) / (Math.abs(b.value) || 1),
  };
  const fn = cmp[mode];
  if (!fn) return [...rows];
  return [...rows].sort((a, b) => (asc ? fn(a, b) : fn(b, a)));
}

const TX_TYPES = ['buy', 'sell', 'dividend', 'drip', 'split', 'transfer_in', 'transfer_out', 'fee', 'deposit'] as const;
const NEEDS = {
  ticker: new Set(['buy', 'sell', 'drip', 'split', 'dividend']),
  qty: new Set(['buy', 'sell', 'drip', 'split']),
  price: new Set(['buy', 'sell', 'drip']),
  amount: new Set(['dividend', 'transfer_in', 'transfer_out', 'fee', 'deposit']),
};

/**
 * Add a transaction — the first write on the ported list page.
 *
 * It posts to `/api/v1/lists/:id/transactions`, which calls `portfolio.addTransaction` — the same service
 * the EJS form posts to, so the lot engine, the validations and the settle() that rebuilds holdings cannot
 * differ between front ends. The fields appear and disappear by the same rules the service validates
 * against (a buy needs shares and a price; a dividend needs an amount).
 */
/**
 * The pane's rows-per-page preference, remembered per pane under the EJS page's keys (`pf.txPageSize`,
 * `pf.divPageSize`). 'all' is a value, not a missing one: 50 on Transactions must not become 50 on Dividends.
 *
 * Returns [value, choose, adopt]: `choose` is the reader picking a size — it stores the choice for this
 * browser AND writes it to the server. `adopt` is the MOUNT sync with the server's copy, and it touches
 * NOTHING but React state: no localStorage, no POST. Both writes it used to make were wrong for the same
 * reason — opening a pane is not a choice. The POST re-sent the value it had just been handed to
 * `/api/v1/prefs/pane-size`, which can only clobber a size the other front end set in between; the cache write
 * made merely LOOKING at one pane stamp the OTHER pane's key, so a reader's Transactions choice was not safe
 * from a visit to Dividends (open Dividends on a transactions-default box and `pf.txPageSize` appeared, with a
 * value nobody picked). views/list.ejs writes nothing on render either: it reads `data-size` off the fragment
 * and only a click in the menu persists anything.
 *
 * `server` is the shared value when the payload already carries it — it usually does, because the pane is
 * expanded from a page that was rendered with it. Taking it as the INITIAL state is what keeps the paint
 * honest once the cache stops being rewritten: the pane would otherwise show the stale cached size for a
 * frame and then correct itself, where EJS's fragment has the shared size in its first HTML.
 */
function useStoredPageSize(
  key: string,
  pane: 'transactions' | 'dividends',
  server?: string,
): [string, (v: string) => void, (v: string) => void] {
  // views/list.ejs VALIDATES what it finds before using it: `v === 'all'`, or a number in [10, 25, 50, 100],
  // otherwise the pane's fallback (10). Reading the raw string meant any other stored value — an older build's
  // '12', a stray '', a hand-edited key — was displayed and used as the page size, where the page would show 10.
  const valid = (v: string | null | undefined): string | null => {
    if (v === 'all') return 'all';
    const n = parseInt(v ?? '', 10);
    return [10, 25, 50, 100].includes(n) ? String(n) : null;
  };
  const read = (): string => {
    try {
      return valid(localStorage.getItem(key)) ?? '10';
    } catch {
      return '10';
    }
  };
  const [v, setV] = useState<string>(() => valid(server) ?? read());
  // State only. The cache is this browser's own record of what its reader PICKED, so it is written by `choose`
  // and by nothing else; the shared copy on the server is what every mount adopts.
  const adopt = (next: string) => setV(next);
  const choose = (next: string) => {
    setV(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      /* private mode */
    }
    void fetch('/api/v1/prefs/pane-size', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pane, size: next }),
    }).catch(() => {});
  };
  return [v, choose, adopt];
}

/** 'all' means every row; a number means that many. */
function pageSizeCount(v: string, total: number): number {
  if (v === 'all') return total;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 10;
}

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP', 'JPY', 'AUD', 'CHF', 'HKD', 'SGD', 'CNY'];

/**
 * The price cell, in the page's own order: the fx rate (only for a non-USD trade), then the currency picker,
 * then the price input. The rate sits FIRST because it is what the price is converted with — and because the
 * cell's own width rule keeps price last, against the column edge.
 */
function PriceCell({
  form,
  currency,
  onCurrency,
  price,
  onPrice,
  fxRate,
  onFxRate,
  ticker,
  required,
}: {
  form: string;
  currency: string;
  onCurrency: (v: string) => void;
  price: string;
  onPrice: (v: string) => void;
  fxRate: string;
  onFxRate: (v: string) => void;
  ticker: string;
  /** The add row's price is a required field (EJS writes `required` on it); an edit row's is not. */
  required?: boolean;
}) {
  return (
    <div className="pf-price-edit" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {currency !== 'USD' ? (
        <input
          className="t-small pf-fx-inp"
          form={form}
          name="fx_rate"
          placeholder="fxRate"
          inputMode="decimal"
          value={fxRate}
          onChange={(e) => onFxRate(e.target.value)}
          style={{ width: 64 }}
        />
      ) : null}
      <Select
        id={`cur-pick-${ticker}`}
        value={currency}
        onChange={onCurrency}
        ariaLabel="Currency"
        options={CURRENCIES.map((c) => ({ value: c, label: c }))}
      />
      <input
        className="t-small pf-price-inp"
        form={form}
        placeholder="Price"
        name="price"
        inputMode="decimal"
        required={required}
        value={price}
        onChange={(e) => onPrice(e.target.value)}
        style={{ width: 88 }}
      />
    </div>
  );
}

function AddTransaction({ listId, open, onClose, edit }: { listId: number; open: boolean; onClose: () => void; edit?: Tx | null }) {
  const qc = useQueryClient();
  const [type, setType] = useState<string>(edit?.type ?? 'buy');
  const [ticker, setTicker] = useState(edit?.ticker ?? '');
  const [quantity, setQuantity] = useState(edit?.quantity != null ? String(edit.quantity) : '');
  const [price, setPrice] = useState(edit?.price != null ? String(edit.price) : '');
  const [amount, setAmount] = useState(edit?.amount != null ? String(edit.amount) : '');
  const [fees, setFees] = useState(edit?.fees != null ? String(edit.fees) : '');
  const [date, setDate] = useState(edit?.tx_date ?? new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { type, tx_date: date };
      if (NEEDS.ticker.has(type)) body.ticker = ticker.toUpperCase();
      if (NEEDS.qty.has(type)) body.quantity = quantity;
      if (NEEDS.price.has(type)) body.price = price;
      if (NEEDS.amount.has(type)) body.amount = amount;
      if (fees) body.fees = fees;
      // An edit is a PATCH to the transaction itself, and the service re-issues the id (delete + insert),
      // so nothing here may assume the row keeps its identity — the queries are invalidated instead.
      const res = await fetch(edit ? `/api/v1/transactions/${edit.id}` : `/api/v1/lists/${listId}/transactions`, {
        method: edit ? 'PATCH' : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['list', listId] });
      qc.invalidateQueries({ queryKey: ['ticker', listId] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    save.mutate();
  };

  return (
    <Modal id="add-tx-modal" open={open} onClose={onClose} title={edit ? 'Edit this transaction' : 'Add a transaction'} size="md">
      {error ? <div className="error" style={{ marginBottom: 12 }}>{error}</div> : null}
      <form onSubmit={submit}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Type" htmlFor="at-type" style={{ flex: '1 1 150px' }}>
            <select id="at-type" value={type} onChange={(e) => setType(e.target.value)}>
              {TX_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace('_', ' ')}</option>
              ))}
            </select>
          </Field>
          {NEEDS.ticker.has(type) ? (
            <Field label="Ticker" htmlFor="at-ticker" required style={{ flex: '1 1 130px' }}>
              <input id="at-ticker" value={ticker} onChange={(e) => setTicker(e.target.value)} autoFocus />
            </Field>
          ) : null}
          <Field label="Date" htmlFor="at-date" required style={{ flex: '1 1 150px' }}>
            <input id="at-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {NEEDS.qty.has(type) ? (
            <Field label="Shares" htmlFor="at-qty" required style={{ flex: '1 1 110px' }}>
              <input id="at-qty" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </Field>
          ) : null}
          {NEEDS.price.has(type) ? (
            <Field label="Price" htmlFor="at-price" required style={{ flex: '1 1 110px' }}>
              <input id="at-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
          ) : null}
          {NEEDS.amount.has(type) ? (
            <Field label="Amount" htmlFor="at-amount" required style={{ flex: '1 1 120px' }}>
              <input id="at-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
          ) : null}
          <Field label="Fees" htmlFor="at-fees" style={{ flex: '1 1 100px' }}>
            <input id="at-fees" inputMode="decimal" value={fees} onChange={(e) => setFees(e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : edit ? 'Save changes' : 'Add transaction'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type Tx = { id: number; type: string; ticker?: string | null; quantity: number | null; price: number | null; amount: number | null; fees: number | null; tx_date: string };
type Div = {
  id: number;
  tx_date: string;
  amount: number | null;
  perShare?: number | null;
  shares?: number | null;
  roc_pct?: number | null;
  fees?: number | null;
};
type Lot = { id: number; tx_id: number; quantity: number; remaining: number; cost_per_share: number; acquired_date: string };
type Detail = {
  transactions: Tx[];
  lots: Lot[];
  dividends: Div[];
  realized: number;
  /** Per-sale figures keyed by the sell transaction's id, as views/partials/ticker-detail.ejs reads them. */
  txRealized?: Record<string, { gain: number; pct: number | null; basis?: number }>;
};

/**
 * The ticker pane's body: that ticker's transactions and lots, with delete behind a confirm dialog.
 *
 * The pane fetches `GET /api/v1/lists/:id/tickers/:ticker` — the endpoint that was built in P1 from the
 * EJS fragment's own locals — and deletes through `DELETE /api/v1/transactions/:id`, which calls
 * portfolio.deleteTransaction and re-settles the list. Editing is not here yet; deleting is, because it is
 * the flow with a confirmation and therefore the one most worth getting right.
 */
/** What the page-level bar needs from whichever pane has a selection. */
export type SelBarState = {
  title: string;
  cells: Cell[];
  total: number;
  showAll: boolean;
  onClear: () => void;
  onSelectAll: () => void;
};

function TickerPane({
  listId,
  ticker,
  name,
  watch,
  price,
  detail,
  open,
  onChanged,
  paneSizes,
  onSelBarChange,
}: {
  listId: number;
  ticker: string;
  /** The rows-per-page preference from the server — the shared copy; localStorage is per origin. */
  paneSizes?: { transactions: string; dividends: string };
  name?: string;
  watch?: boolean;
  /** The holding's current price — a row's unrealized is its open lot marked to this, as the EJS pane does. */
  price?: number | null;
  detail: Detail | null;
  /** The row is expanded. Closing it abandons an open add row and a row mid-edit, as the EJS pane does when it
   *  is reloaded away — keeping them would reopen the pane on a half-filled form the reader had left. */
  open: boolean;
  onChanged: () => void;
  /** Publishes the selection summary to the PAGE, which owns `#pf-selbar`: the bar is the page's region (one per
   *  page, as views/list.ejs mounts it as a direct child of <main>), not part of the pane's markup. */
  onSelBarChange: (s: SelBarState | null) => void;
}) {
  const [confirming, setConfirming] = useState<Tx | null>(null);
  const [editing, setEditing] = useState<Tx | null>(null);
  const [removingTicker, setRemovingTicker] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // Which of the three panes is showing. The EJS pane is three sibling .detail-pane blocks driven by a
  // segmented tab bar, and the browser suite (and the pane CSS) address them by data-pane, so the SPA
  // renders the same three blocks rather than one combined panel.
  const [pane_tab, setPaneTab] = useState<'transactions' | 'dividends' | 'lots'>('transactions');
  // The pane's two secondary actions live behind one ⋯ beside the name (EJS: `.pane-menu-btn` →
  // `.pane-menu`): the delete is not a permanently visible ✕ next to the title, and Show details — which
  // reveals the Fees column and the currency picker — belongs with the other thing you do to this holding.
  const [paneMenu, setPaneMenu] = useState(false);
  // Which row's ⋮ has its edit/delete pair pinned open. EJS's ⋮ is a toggle, not a hover proxy
  // (`.row-actions-open` on the row): on touch there is no hover to reveal the pair with, and a click is how
  // the reader asks for it. Only one row is open at a time, and a click anywhere outside the pair closes it.
  const [openActions, setOpenActions] = useState<string | null>(null);
  // The pane menu's trigger, for the Menu overlay's placement — the EJS pane menu is a PFOverlay too.
  const paneBtn = useRef<HTMLSpanElement | null>(null);
  // Rows per page per pane, remembered under the EJS page's own keys so a reader's choice survives a reload
  // and the two panes keep separate preferences (ten dividends and fifty transactions are different jobs).
  const [txPageSize, setTxPageSize, adoptTxPageSize] = useStoredPageSize('pf.txPageSize', 'transactions', paneSizes?.transactions);
  const [divPageSize, setDivPageSize, adoptDivPageSize] = useStoredPageSize('pf.divPageSize', 'dividends', paneSizes?.dividends);
  // The server's copy is adopted when the payload arrives AFTER the pane mounted — a row can be expanded while
  // `/api/v1/lists/:id` is still in flight, and on that path the `server` argument above was undefined. ADOPTED,
  // not chosen: it sets state and nothing else (no cache write, no POST — see the hook), and BOTH panes take
  // their value in one effect. They used to be chained through this flag, dividends behind transactions, which
  // synced the second pane a render late. Once only: a later refetch of the list must not overwrite a size the
  // reader picks while it is in flight.
  const [sizesSynced, setSizesSynced] = useState(false);
  useEffect(() => {
    if (!paneSizes || sizesSynced) return;
    adoptTxPageSize(paneSizes.transactions);
    adoptDivPageSize(paneSizes.dividends);
    setSizesSynced(true);
  }, [paneSizes, sizesSynced]);
  // Page 1-based, as the pager's buttons are.
  const [txPage, setTxPage] = useState(1);
  // The inline add row (EJS: `.add-tx-link` reveals `#add-tx-row-<ticker>`). A dialog was the SPA's shortcut;
  // adding a transaction is a row in the table the reader is already reading, with the same columns.
  const [addingTx, setAddingTx] = useState(false);
  /**
   * One action at a time in this pane. Adding a row, ticking rows and editing a row are mutually exclusive: each
   * one takes the pane's attention, and two open at once means a form is being typed into while the bar counts
   * rows behind it, or a row is half-edited while another is ticked. EJS already ties add↔edit through
   * `cancelCurrentAction`; selection joins them here.
   */
  const beginSelection = () => {
    setAddingTx(false);
    setEditRow(null);
    setSelecting(true);
  };
  const beginEdit = (row: {
    id: number;
    tx_date: string;
    type: string;
    quantity: string;
    price: string;
    fees: string;
    notes: string;
    currency: string;
    fx_rate: string;
  }) => {
    setAddingTx(false);
    setPicked(new Set());
    setSelecting(false);
    setEditRow(row);
  };
  // EJS's add/edit handlers call detailsAutoOn(ticker) and put `tx-table-hide-gains` on the table: the form is
  // entered with Show details ON (so the Fees column is there for the fee they are about to type) and with the
  // Value/Unrealized columns hidden (they are figures about rows that exist). Without both, the add row carried
  // a different column set from the one it is typed into, and nothing lined up.
  const detailsBeforeAction = useRef<boolean | null>(null);
  const [addCurrency, setAddCurrency] = useState('USD');
  // The row being edited IN PLACE (EJS: `.tx-editing`), seeded with the EXACT price — seeding from the rounded
  // display value meant a cosmetic edit rewrote the price (98.8901 became 98.89).
  const [editRow, setEditRow] = useState<{
    id: number;
    tx_date: string;
    type: string;
    quantity: string;
    price: string;
    fees: string;
    notes: string;
    currency: string;
    fx_rate: string;
  } | null>(null);
  const [addFx, setAddFx] = useState('');
  useEffect(() => {
    if (!open) {
      setAddingTx(false);
      // What was typed belongs to the pane that was closed: reopening must not offer last visit's half-row.
      setNewTx({
        tx_date: new Date().toISOString().slice(0, 10),
        type: 'buy',
        quantity: '',
        price: '',
        fees: '',
        notes: '',
      });
      setEditRow(null);
      setEditing(null);
      setConfirming(null);
      setPaneMenu(false);
      setOpenActions(null);
    }
  }, [open]);
  // The pinned pair closes on a click anywhere outside it — the ⋮ of another row pins THAT row instead of
  // leaving two open. The ⋮ stops its own click, so this listener only sees clicks that mean "elsewhere".
  useEffect(() => {
    if (!openActions) return;
    const away = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest && (t.closest('.row-actions') || t.closest('.lot-menu-btn'))) return;
      setOpenActions(null);
    };
    document.addEventListener('click', away);
    return () => document.removeEventListener('click', away);
  }, [openActions]);
  const [newTx, setNewTx] = useState({
    tx_date: new Date().toISOString().slice(0, 10),
    type: 'buy',
    quantity: '',
    price: '',
    fees: '',
    notes: '',
  });
  // The ✓ is disabled until every field the row MARKS required has a value — the date, the quantity and
  // the price — which is what EJS's `wireRequireGate` does in views/list.ejs (`btn.disabled = !req.every(...)`
  // over the row's `input[required]`). Without it the SPA's ✓ looked ready on a row with an empty Qty and the
  // click POSTed, so the reader got the server's "Quantity must be a positive number." instead of a dead
  // button. The POST path is untouched: this is the button's own state, exactly as EJS gates its own.
  const addRowComplete = [newTx.tx_date, newTx.quantity, newTx.price].every((v) => String(v).trim() !== '');
  // Show details starts ON for a holding that has rows to show, OFF for an empty one: the Fees column and the
  // currency picker are the extra reading a populated pane shows by default, and there is nothing to read in a
  // pane with no transactions. A reader's toggle overrides both.
  const [showDetails, setShowDetails] = useState<boolean | null>(null);
  // The delete goes through the app's confirm dialog (`#confirm-modal` / `#confirm-modal-ok`), as the EJS
  // form does through data-confirm — a destructive row action that fires on click is a trap.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: async (txId: number) => {
      const res = await fetch(`/api/v1/transactions/${txId}`, { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      return json.data;
    },
    onSuccess: () => {
      setConfirming(null);
      onChanged();
    },
    onError: (e: Error) => setError(e.message),
  });

  // One write, through the same endpoint the EJS inline form posts to. Blank numeric fields are omitted rather
  // than sent as "" — the service treats an absent field as "not given" and an empty string as zero.
  // Saving the in-place edit: same endpoint the EJS inline editor posts to.
  const saveEdit = async () => {
    if (!editRow) return;
    const body: Record<string, unknown> = { ticker, tx_date: editRow.tx_date, type: editRow.type };
    for (const k of ['quantity', 'price', 'fees'] as const) {
      if (String(editRow[k]).trim() !== '') body[k] = Number(editRow[k]);
    }
    // Currency only when the trade is not in dollars: the service carries the row's own currency columns when
    // the payload says nothing about them, and sending `currency` makes it read `priceNative`/`fxRate` — which
    // a USD edit has no reason to carry (a bare fx_rate key was silently ignored and the write was refused).
    if (editRow.currency && editRow.currency !== 'USD') {
      body.currency = editRow.currency;
      if (String(editRow.price).trim() !== '') body.priceNative = Number(editRow.price);
      if (String(editRow.fx_rate).trim() !== '') body.fxRate = Number(editRow.fx_rate);
    }
    body.notes = editRow.notes.trim();
    const res = await fetch(`/api/v1/transactions/${editRow.id}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      setError(json?.error || `HTTP ${res.status}`);
      return;
    }
    setEditRow(null);
    onChanged();
  };

  const saveInline = async () => {
    const body: Record<string, unknown> = { ticker, type: newTx.type, tx_date: newTx.tx_date };
    for (const k of ['quantity', 'price', 'fees'] as const) {
      if (String(newTx[k]).trim() !== '') body[k] = Number(newTx[k]);
    }
    if (addCurrency && addCurrency !== 'USD') {
      body.currency = addCurrency;
      if (String(newTx.price).trim() !== '') body.priceNative = Number(newTx.price);
      if (String(addFx).trim() !== '') body.fxRate = Number(addFx);
    }
    if (newTx.notes.trim()) body.notes = newTx.notes.trim();
    const res = await fetch(`/api/v1/lists/${listId}/transactions`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      setError(json?.error || `HTTP ${res.status}`);
      return;
    }
    setAddingTx(false);
    setNewTx((v) => ({ ...v, quantity: '', price: '', fees: '', notes: '' }));
    onChanged();
  };

  const txs = detail?.transactions ?? [];
  // Where page 1 starts (0-based) — computed here, after `txs` exists.
  const txStart = (txPage - 1) * pageSizeCount(txPageSize, txs.length);
  const lots = detail?.lots ?? [];
  const divs = (detail?.dividends ?? []) as Div[];
  // Off until the reader asks, as the page's checkbox starts. The Fees column is the extra reading.
  const detailsOn = showDetails === true;
  const txActionOpen = addingTx || !!editRow;
  useEffect(() => {
    if (txActionOpen && detailsBeforeAction.current === null && !detailsOn) {
      detailsBeforeAction.current = false;
      setShowDetails(true);
    } else if (!txActionOpen && detailsBeforeAction.current !== null) {
      setShowDetails(detailsBeforeAction.current);
      detailsBeforeAction.current = null;
    }
  }, [txActionOpen, detailsOn]);
  // The selection bar's totals. Every figure is computed from the ticked rows themselves (never from a
  // running total kept alongside them): a selection survives paging, so the rows are the only truth.
  // The rows on this page: "select every transaction on this page" means this page, not the whole history.
  const visibleRows = txs.slice(txStart, txStart + pageSizeCount(txPageSize, txs.length));
  const pickedRows = txs.filter((t) => picked.has(t.id));
  const selQty = pickedRows.reduce((n, t) => n + (Number(t.quantity) || 0), 0);
  const selPriced = pickedRows.filter((t) => Number.isFinite(Number(t.price)) && Number.isFinite(Number(t.quantity)));
  const selPricedQty = selPriced.reduce((n, t) => n + Number(t.quantity), 0);
  // Quantity-WEIGHTED: a 400-share fill and a 1-share fill are not two equal opinions about the price.
  const selWavg = selPricedQty > 0
    ? selPriced.reduce((n, t) => n + Number(t.quantity) * Number(t.price), 0) / selPricedQty
    : null;
  const selFees = pickedRows.reduce((n, t) => n + (Number((t as { fees?: number }).fees) || 0), 0);
  const selValue = pickedRows.reduce((n, t) => {
    const q = Number(t.quantity) || 0;
    const p = Number(t.price) || 0;
    return n + (t.amount != null ? Number(t.amount) : q * p) + (Number((t as { fees?: number }).fees) || 0);
  }, 0);
  const selTypes: Record<string, number> = {};
  for (const t of pickedRows) selTypes[t.type] = (selTypes[t.type] || 0) + 1;
  const selTypeTxt = Object.entries(selTypes).map(([k, n]) => `${n} ${k}${n === 1 ? '' : 's'}`).join(' · ');
  const selDates = pickedRows.map((t) => t.tx_date).filter(Boolean).sort();
  const selRange = selDates.length
    ? (selDates[0] === selDates[selDates.length - 1] ? selDates[0] : `${selDates[0]} → ${selDates[selDates.length - 1]}`)
    : '';
  const selNet = pickedRows.reduce((n, t) => n + (t.type === 'sell' ? -(Number(t.quantity) || 0) : (Number(t.quantity) || 0)), 0);
  const selNetDiff = Math.abs(Math.abs(selNet) - selQty) > 1e-9;
  const selTitle = pickedRows.length
    ? [ticker, `${pickedRows.length} selected`, selTypeTxt, selRange].filter(Boolean).join(' · ')
    : '';
  // Publish to the page, which owns the bar. The signature is the selection itself (ticker + ids), so this runs
  // when the selection changes and never on an unrelated render; the payload carries JSX cells, so an object
  // identity dep would republish on every pass.
  const selSig = picked.size ? `${ticker}:${[...picked].sort((a, b) => a - b).join(',')}` : '';
  useEffect(() => {
    if (!selSig) {
      onSelBarChange(null);
      return;
    }
    onSelBarChange({
      title: selTitle,
      cells: selCells,
      total: txs.length,
      // EJS hides the button once every row is ticked, and labels it with the pane's own total.
      showAll: picked.size < txs.length,
      onClear: () => {
        setPicked(new Set());
        setSelecting(false);
      },
      // "Select all N" takes the pane, not the visible page — views/list.ejs's `selRowsOf(pane)`.
      onSelectAll: () => {
        setAddingTx(false);
        setEditRow(null);
        setPicked(new Set(txs.map((t) => t.id)));
        setSelecting(true);
      },
    });
  }, [selSig]);
  // Cleared only when the pane goes away (the row is collapsed): clearing on every selection change would blink
  // the bar out and back between two commits, and a reader mid-click would see it disappear.
  useEffect(() => () => onSelBarChange(null), []);

  // The checkbox rides the row's first TEXT line, which has to be measured (views/list.ejs's alignCheckboxes):
  // the date cell's line sits ~3px from the cell top in a row with a wrapped price and ~11px in one without, so
  // any constant — including centring in the cell — is wrong for one of them. The pane publishes two custom
  // properties and the stylesheet reads them.
  const txPaneRef = useRef<HTMLDivElement | null>(null);
  // The header box's dash. `indeterminate` is a DOM property with no HTML attribute, so React cannot set it from
  // JSX: a partial page selection left the box looking empty (views/list.ejs's syncSelectAll sets both).
  const allCbRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const cb = allCbRef.current;
    if (!cb) return;
    const on = visibleRows.filter((r) => picked.has(r.id)).length;
    cb.indeterminate = on > 0 && on < visibleRows.length;
  }, [picked, visibleRows]);
  useEffect(() => {
    const pane = txPaneRef.current;
    if (!pane) return;
    const lineOf = (el: Element | null) => {
      if (!el) return null;
      const r = document.createRange();
      r.selectNodeContents(el);
      return r.getClientRects()[0] || null;
    };
    const row = [...pane.querySelectorAll('tr.tx-row:not(.tx-split-row)')].find(
      (r) => (r as HTMLElement).offsetParent !== null,
    );
    if (row) {
      const cell = row.children[0] as HTMLElement;
      const line = lineOf(row.children[1] ?? null);
      if (line && cell) {
        const cellTop = cell.getBoundingClientRect().top;
        pane.style.setProperty('--pf-cb-top', Math.max(0, line.top - cellTop + (line.height - 14) / 2).toFixed(1) + 'px');
      }
    }
    const th = pane.querySelector('thead th:nth-child(2)');
    const thCell = pane.querySelector('thead th.c-select') as HTMLElement | null;
    if (th && thCell) {
      const line = lineOf(th);
      if (line) {
        const top = thCell.getBoundingClientRect().top;
        pane.style.setProperty('--pf-cb-top-h', Math.max(0, line.top - top + (line.height - 14) / 2).toFixed(1) + 'px');
      }
    }
  });

  // The per-row figures, as views/partials/ticker-detail.ejs computes them: a transaction's unrealized is its
  // OPEN lot (remaining shares at cost per share) marked to the current price; its realized comes from the
  // disposals its sale produced. Both were printed as "—" because the pane read `t.unrealized`/`t.realized`,
  // and a transaction row carries neither.
  const txOpen: Record<number, { rem: number; cps: number }> = {};
  for (const l of lots) if (l.remaining > 1e-9) txOpen[l.tx_id] = { rem: l.remaining, cps: l.cost_per_share };
  const uxOf = (t: { id: number }) => {
    const op = txOpen[t.id];
    if (!op || price == null) return null;
    return { gain: op.rem * (price - op.cps), pct: op.cps > 0 ? ((price - op.cps) / op.cps) * 100 : null };
  };
  const rxOf = (t: { id: number }) => (detail?.txRealized || {})[String(t.id)] || null;
  // The bar's Unrealized and Realized columns are the SAME figures the rows show — a transaction's unrealized is
  // its open lot marked to today's price, its realized comes from the disposals its sale produced. The bar read
  // `t.unrealized` / `t.realized`, which no transaction row carries, so both columns painted $0.00 next to a pane
  // whose rows said $52.7K.
  const selUxRows = pickedRows.map((t) => uxOf(t)).filter((v): v is { gain: number; pct: number | null } => v != null);
  const selRxRows = pickedRows.map((t) => rxOf(t)).filter((v): v is { gain: number; pct: number | null } => v != null);
  const selUnreal = selUxRows.reduce((n, v) => n + v.gain, 0);
  const selRealized = selRxRows.reduce((n, v) => n + v.gain, 0);
  const selCells: Cell[] = pickedRows.length
    ? [
        selRange,
        `${pickedRows.length} selected${selTypeTxt ? ' · ' + selTypeTxt : ''}`,
        // The net leads because it is what the selection did to the position; the raw total is context, and
        // only shown when the two differ (a lone buy's net IS its quantity).
        selNetDiff ? (
          <>
            {`${selNet < 0 ? '−' : ''}${nfmt(Math.abs(selNet))}`}
            <div className="muted t-micro">{`total ${nfmt(selQty)}`}</div>
          </>
        ) : (
          nfmt(selQty)
        ),
        selWavg != null ? money(selWavg) : '—',
        money(selFees),
        money(selValue),
        // '—' when nothing in the selection has an open lot (or a realised figure), as the page's `nums(...)`
        // guard does — $0.00 would claim a zero rather than "not applicable".
        selUxRows.length ? <span className={selUnreal < 0 ? 'neg' : 'pos'}>{money(selUnreal)}</span> : '—',
        selRxRows.length ? <span className={selRealized < 0 ? 'neg' : 'pos'}>{money(selRealized)}</span> : '—',
      ]
    : [];
  // The dividends pane's five figures. "This year" is the calendar year, not the last twelve months — the EJS
  // page shows both, and they answer different questions.
  const year = new Date().getFullYear();
  const divThisYear = divs.filter((d) => String(d.tx_date).startsWith(String(year)));
  const divLast12 = divs.filter((d) => {
    const t = new Date(String(d.tx_date) + 'T00:00:00');
    const cut = new Date();
    cut.setFullYear(cut.getFullYear() - 1);
    return t >= cut;
  });
  const divLastYear = divs.filter((d) => String(d.tx_date).startsWith(String(year - 1)));
  const divTotal = (rows: typeof divs) => rows.reduce((n, d) => n + (d.amount ?? 0), 0);
  const divTiles: [string, string][] = [
    ['Total', money(divTotal(divs))],
    ['Payments', String(divs.length)],
    ['Avg / payment', divs.length ? money(divTotal(divs) / divs.length) : '—'],
    ['This year', money(divTotal(divThisYear))],
    ['Last 12 months', money(divTotal(divLast12))],
    [`Last year (${year - 1})`, money(divTotal(divLastYear))],
  ];

  // Column widths, or nothing while the table is showing something other than plain rows. Two states hold an
  // extra column's worth of width: selection (the 30px checkbox column, which Chrome would otherwise shrink to
  // 15px) and the add row (whose inputs are ~35px wider than their cells, which is exactly how EJS's table
  // redistributes while the add row is open — its Date column grows from 154px to 185px).
  // Two of the add form's columns are sized by their CONTENT, not by the table: the type picker is 88px wide
  // whatever the window, and the price cell (currency picker + price field) is 254px. EJS gives them exactly
  // those widths at 900, 1200 and 1400 alike and lets the remaining columns share what is left, so they are
  // pinned in px while the rest fall back to auto layout.
  // While a row is being ADDED or EDITED the table re-flows to its content, as the page's does: the view's
  // percentages are a fixed column plan, and the page has never pinned them once a form is in the row (its own
  // table has no widths at all — the inputs size the columns). Pinning them here made the editor's columns
  // 10px wider each than the page's.
  const colW = (pct: string, addPct?: string) => (selecting || editRow ? undefined : addingTx ? addPct : pct);

  return (
    <>
      <div className="detail-pin">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 11px', minHeight: 20 }}>
          <span className="t-h3" style={{ fontWeight: 600, color: 'var(--text)' }}>
            {name || ticker}
          </span>
          <span ref={paneBtn} style={{ marginLeft: 'auto', position: 'relative', display: 'inline-flex' }}>
            <Button
              // The same `ghost` control as the header's ⋯ (EJS: class="ghost pane-menu-btn"): 33x35, 9px
              // padding, a visible border. It had been forced to 28px with an #root override so it matched a
              // header button that was itself the wrong variant — two wrongs pinned to each other by a test.
              variant="ghost"
              className="pane-menu-btn"
              ariaLabel="Ticker actions"
              attrs={{
                'aria-haspopup': 'menu',
                'aria-expanded': String(paneMenu),
                'data-menu-trigger': `pane-menu-${ticker}`,
              }}
              onClick={() => setPaneMenu((v) => !v)}
            >
              {/* The horizontal ellipsis (…), the same glyph and the same .ghost button box as the list menu. */}
              …
            </Button>
            {/* The pane menu is the Menu overlay, as the EJS pane menu is PFOverlay: it had been a plain div
                flowed after the button inside this span, so it pushed layout, could be clipped, and only
                landed in the right place by accident. */}
            <Menu
              id={`pane-menu-${ticker}`}
              open={paneMenu}
              onClose={() => setPaneMenu(false)}
              anchorRef={paneBtn}
              align="right"
              className="pane-menu"
              minWidth={196}
              shadow="0 8px 30px var(--scrim)"
              portal={false}
              keepMounted
            >
                {/* Closed on the input's CHANGE, not on a click on the label: a click handler on the label runs
                    before the change is delivered, hides the menu, and the change is then never dispatched —
                    the box was left ticking with the column still hidden. */}
                <label className="pane-menu-item show-details-label" data-ticker={ticker} role="menuitemcheckbox"
                  aria-checked={showDetails === true} tabIndex={0} data-menu-item="">
                  <input
                    type="checkbox"
                    className="show-splits-toggle"
                    data-ticker={ticker}
                    // Unchecked until the reader ticks it, as the EJS control is (it starts with no explicit
                    // state and only carries one once changed) — the column can be showing and the box empty,
                    // which is the page's own inconsistency and not this port's to tidy.
                    checked={showDetails === true}
                    onChange={(e) => {
                      setShowDetails(e.target.checked);
                      // Choosing closes the menu, as the EJS menu does: the reader has answered the question
                      // it was open for, and a menu left standing covers the column it just revealed.
                      setPaneMenu(false);
                    }}
                  />
                  <span>Show details</span>
                </label>
                <button
                  type="button"
                  role="menuitem"
                  data-menu-item=""
                  className="pane-menu-item pane-menu-danger"
                  onClick={() => {
                    setPaneMenu(false);
                    setConfirmRemove(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                  </svg>
                  <span>{watch ? 'Remove from watchlist' : 'Delete ticker'}</span>
                </button>
            </Menu>
          </span>
        </div>
        {/* The tabs come AFTER the name row, inside the pane's own block, as the EJS pane orders them: the
            name and its ⋯ are the heading, the tabs are the switch under it. With them above, the ⋯ sat below
            the tab bar and the pane's menu opened 42px lower than the EJS one. */}
        <Tabs
          id={`detail-tabs-${ticker}`}
          mode="button"
          variant="segmented"
          raised
          className="detail-tabs"
          ariaLabel="Ticker detail"
          active={pane_tab}
          style={{ marginBottom: 10 }}
          onChange={(k) => setPaneTab(k as typeof pane_tab)}
          items={[
            { key: 'transactions', label: 'Transactions' },
            { key: 'dividends', label: 'Dividends' },
            { key: 'lots', label: 'Share Lots' },
          ]}
        />
      </div>
      {!detail ? (
        <div className="muted t-small" style={{ padding: '10px 14px' }}>
          Loading…
        </div>
      ) : null}
      <div
        ref={txPaneRef}
        id={`detail-pane-${ticker}-transactions`}
        data-pane="transactions"
        data-ticker={ticker}
        // `selecting` is the page's own selection-mode class (views/list.ejs puts it on the pane); `is-selecting`
        // on the table is what hides the checkbox column here.
        className={'detail-pane' + (detailsOn ? '' : ' no-details') + (selecting ? ' selecting' : '')}
        // Hidden until the payload is in. An empty-state placeholder has height, so it reads as the pane's
        // content: the tabs and the row show "no lots for this ticker" for a beat before the lots exist.
        style={pane_tab === 'transactions' && detail ? undefined : { display: 'none' }}
      >
        <div>
      {error ? <div className="error" style={{ marginBottom: 8 }}>{error}</div> : null}

      <Table
        expandable={false}
        compact
        // The select column is always in the markup and hidden by class while nothing is selected: the
        // cell has to survive a click on the last checkbox, or the input is unmounted mid-event.
        tableClass={'row-hover' + (selecting ? ' is-selecting' : '') + (txActionOpen ? ' tx-table-hide-gains' : '')}
        onClickRow={(key) => {
          const id = Number(key);
          if (!Number.isFinite(id)) return;
          // Ticking a row is an ACTION: it closes the add row and the row being edited (see beginSelection).
          beginSelection();
          // Unticking the LAST row returns the pane to its default state — no checkbox column, no bar. The page
          // does the same (`if (!selState.ids.size) clearSelection()`); leaving the column up with nothing ticked
          // is a mode the reader never asked for.
          const next = new Set(picked);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          setPicked(next);
          if (!next.size) setSelecting(false);
        }}
        columns={[
          // The EJS table's columns, class and order: its column hooks (c-date, c-qty, c-price, c-notes) are what
          // the pane's widths and the suite's "this header sits over its own column" check address.
          {
            className: 'c-select',
            // The checkbox column is 30px on the EJS table.
            width: 30,
            label: (
              <input
                ref={allCbRef}
                type="checkbox"
                className="tx-select-all"
                data-ticker={ticker}
                aria-label="Select every transaction on this page"
                checked={visibleRows.every((r) => picked.has(r.id))}
                onChange={(e) => {
                  if (e.target.checked) beginSelection();
                  setPicked((prev) => {
                    const next = new Set(prev);
                    for (const r of visibleRows) {
                      if (e.target.checked) next.add(r.id);
                      else next.delete(r.id);
                    }
                    if (!next.size) setSelecting(false);
                    return next;
                  });
                }}
              />
            ),
          },
          // Sizes are the EJS table's own, measured on both front ends at 900, 1200 and 1400px: auto layout
          // distributes the spare width by CONTENT, and the two DOMs' intrinsic widths differ (EJS gives the
          // action column 15.7%, the SPA gave it 12.7%, which pushed Date→Notes 3-32px right). EJS's
          // proportions move by <0.1% across those widths, so pinning them here holds at every width instead
          // of only at the one it was measured at.
          // Widths only while the selection column is hidden: with it shown the table holds 100% of columns
          // PLUS the 30px checkbox column, and the browser then squeezes that column to 15px. EJS distributes
          // by content in that state, so the SPA falls back to auto layout too.
          // While the add row is open, EJS's table redistributes: it gives Date 185.5px and Type 87.6px
          // (14.52% / 6.85%) because the date input's intrinsic width is larger than a row's text. The SPA has
          // to hold those two the same way or its inputs sit in narrower cells than the EJS form's.
          { label: lbl('Date'), className: 'c-date', width: colW('12.05%', '14.5%') },
          { label: lbl('Type'), className: 'c-type', width: colW('7.85%', '6.85%') },
          { label: lbl('Qty'), num: true, className: 'c-qty', width: colW('8.3%', '14.5%') },
          // In the add state the price column is as wide as its own two fields need (EJS's is content-sized and
          // measures 254px at every width). Pinned at 19.95% it only matched EJS at desktop widths and lost 65px
          // by 900 — the 24px the row's action pair correctly takes then came out of this column's neighbours.
          { label: 'Price', num: true, className: 'c-price', width: colW('8.32%', '') },
          // No width: the fee column is display:none unless details are on, and a pinned 0 would squash it the
          // moment they are.
          { label: lbl('Fees'), num: true, className: 'c-fees', width: colW('', '14.5%') },
          { label: lbl('Value'), num: true, width: colW('12.8%') },
          { label: lbl('Unrealized'), num: true, width: colW('16.4%') },
          { label: lbl('Realized'), num: true, width: colW('11.12%', '6.3%') },
          { label: lbl('Note'), className: 'c-notes', width: colW('7.4%', '14.5%') },
          { label: '', width: colW('15.74%') },
        ]}
        rows={[
          ...txs.map((t, ti) => (editRow && editRow.id === t.id ? {
            key: t.id,
            rowClass: 'tx-row tx-editing',
            cells: [
              // The select column's cell, and it must carry BOTH classes a normal row's does: without
              // `c-select-cell` the hidden-column rule (`td.c-select-cell`) never matched it, so the editor kept
              // a real 87px cell where the header has none — every column from Date rightwards sat one column
              // off, with an empty box before the date.
              { className: 'c-select c-select-cell', content: <span key="c" /> },
              <input key="d" className="t-small" name="tx_date" value={editRow.tx_date}
                onChange={(e) => setEditRow({ ...editRow, tx_date: e.target.value })} />,
              <Select key="t" id={`edit-type-${t.id}`} value={editRow.type}
                onChange={(v) => setEditRow({ ...editRow, type: v })} ariaLabel="Transaction type"
                options={TX_TYPES.map((x) => ({ value: x, label: x }))} />,
              <input key="q" className="t-small" name="quantity" value={editRow.quantity}
                onChange={(e) => setEditRow({ ...editRow, quantity: e.target.value })} />,
              <PriceCell key="p" form="" ticker={ticker} currency={editRow.currency}
                onCurrency={(v) => setEditRow({ ...editRow, currency: v })}
                price={editRow.price} onPrice={(v) => setEditRow({ ...editRow, price: v })}
                fxRate={editRow.fx_rate} onFxRate={(v) => setEditRow({ ...editRow, fx_rate: v })} />,
              <input key="f" className="t-small" name="fees" value={editRow.fees}
                onChange={(e) => setEditRow({ ...editRow, fees: e.target.value })} />,
              '',
              '',
              '',
              <input key="n" className="t-small" name="notes" value={editRow.notes}
                onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })} />,
              // ✕ first, ✓ second — the order every confirm pair in the app uses. The two buttons are the
              // cell's own children, as in the EJS edit row (list.ejs writes them into menuCell.innerHTML with
              // no wrapper), and the ✕ carries that row's `style="margin-right: 4px;"`: JSX puts no whitespace
              // between adjacent elements, so a pair with neither a wrapper nor a margin touches (measured 0px).
              <Fragment key="a">
                <button type="button" className="ghost btn-confirm-x cancel-edit" title="Cancel" style={{ marginRight: 4 }} onClick={() => setEditRow(null)}>✕</button>
                <button type="button" className="ghost btn-confirm-ok save-edit" title="Save" onClick={() => void saveEdit()}>✓</button>
              </Fragment>,
            ],
          } : {
          key: t.id,
          // `tx-row` is the row class the page's CSS reveals the actions from (:has(.lot-menu-btn:hover)),
          // and the suite reads cells by it.
          rowClass:
            'tx-row row-action' +
            // A ticked row paints the soft accent tint on its cells (views/list.ejs's `.tx-row.selected > td`).
            // The SPA never put the class on, so a selected row looked exactly like its neighbours apart from
            // the checkbox itself.
            (picked.has(t.id) ? ' selected' : '') +
            (openActions === `tx-${t.id}` ? ' row-actions-open' : '') +
            (ti < txStart || ti >= txStart + pageSizeCount(txPageSize, txs.length) ? ' pane-hidden' : ''),
          // The row's own facts, as the page carries them: the stylesheet and the suite select split rows by
          // data-type, and the selection bar's totals read the same attributes it always has.
          rowAttrs: {
            'data-tx-id': t.id,
            'data-tx': t.id,
            'data-type': t.type,
            'data-date': t.tx_date,
            'data-qty': String(t.quantity ?? ''),
            'data-price-full': String(t.price ?? ''),
          },
          cells: [
            // The select column exists only while selecting — header and cells together, so the row always has
            // one cell per column and nothing shifts when the reader starts selecting.
            {
              content: (
              <input
                key="c"
                type="checkbox"
                className="tx-select"
                checked={picked.has(t.id)}
                // The row itself is a click target; without this the checkbox's own click bubbles to it and the
                // tick is applied and then immediately undone.
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (e.target.checked) beginSelection();
                  setPicked((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(t.id);
                    else next.delete(t.id);
                    if (!next.size) setSelecting(false);
                    return next;
                  });
                }}
              />
              ),
              className: 'c-select c-select-cell',
            },
            t.tx_date,
            // A type is a chip on the EJS row (`.tag`), not a bare word — it is what makes 'buy' and 'sell'
            // readable down the column.
            <span key="ty" className="tag">
              {String(t.type).replace('_', ' ')}
            </span>,
            t.quantity == null ? '—' : nfmt(t.quantity),
            t.price == null ? '—' : money(t.price),
            // Fees are always a figure on the EJS row — money(0) is "$0.00", not a dash — and the value they
            // belong to includes them.
            money(t.fees ?? 0),
            t.quantity != null && t.price != null
              ? money(t.quantity * t.price + (t.fees ?? 0))
              : t.amount != null
                ? money(t.amount)
                : '—',
            // The row's own figures, from its lot and its disposals — the same two numbers ticker-detail.ejs
            // computes. The pane had printed "—" because it looked for fields the rows never carry.
            uxOf(t)?.pct != null ? (
              <span key="ux" className={uxOf(t)!.gain >= 0 ? 'pos' : 'neg'} title={`${money(uxOf(t)!.gain)} ( ${uxOf(t)!.pct!.toFixed(2)}% )`}>
                {abbrM(uxOf(t)!.gain)} ({abbrPct(uxOf(t)!.pct)})
              </span>
            ) : (
              '—'
            ),
            rxOf(t)?.pct != null ? (
              <span key="rx" className={rxOf(t)!.gain >= 0 ? 'pos' : 'neg'} title={`${money(rxOf(t)!.gain)} ( ${rxOf(t)!.pct!.toFixed(2)}% )`}>
                {abbrM(rxOf(t)!.gain)} ({abbrPct(rxOf(t)!.pct)})
              </span>
            ) : (
              '—'
            ),
            {
              className: 'c-notes t-small',
              attrs: { style: 'color: var(--muted); max-width: 180px' },
              content: (t as { notes?: string | null }).notes || '—',
            },
            // The action cell as the EJS row builds it: the edit/delete pair lives in `.row-actions`, which is
            // revealed by hovering the row's own ⋮ — and the ⋮ is all the row shows at rest. The pair used to be
            // painted permanently here, in the base `button` rule's white, so each row wore two faint icons.
            {
              attrs: { style: 'text-align: right; padding: 0 10px' },
              content: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minHeight: 35 }}>
                  <span className="row-actions">
                    <button
                      type="button"
                      className="icon-btn row-edit-btn"
                      title="Edit"
                      aria-label="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActions(null);
                        // Editing is an ACTION too: it closes the add row and drops any selection.
                        beginEdit({
                          id: t.id,
                          tx_date: t.tx_date,
                          type: t.type,
                          quantity: t.quantity == null ? '' : String(t.quantity),
                          price: t.price == null ? '' : String(t.price),
                          fees: (t as { fees?: number }).fees == null ? '' : String((t as { fees?: number }).fees),
                          notes: (t as { notes?: string }).notes || '',
                          currency: (t as { currency?: string }).currency || 'USD',
                          fx_rate: (t as { fx_rate?: number }).fx_rate == null ? '' : String((t as { fx_rate?: number }).fx_rate),
                        });
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="icon-btn row-del-btn"
                      title="Delete"
                      aria-label="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirming(t);
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                      </svg>
                    </button>
                  </span>
                  <button
                    type="button"
                    className="ghost lot-menu-btn"
                    style={{ fontWeight: 700 }}
                    title="Row actions"
                    aria-label="Row actions"
                    aria-expanded={openActions === `tx-${t.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenActions((prev) => (prev === `tx-${t.id}` ? null : `tx-${t.id}`));
                    }}
                  >
                    ⋮
                  </button>
                </div>
              ),
            },
          ],
          })),
          // The add row: present only while adding, and its cells are the row's own columns.
          {
            key: 'inline-new-tx',
            rowClass: 'inline-new-tx-row' + (addingTx ? '' : ' is-hidden'),
            rowAttrs: {
              id: `add-tx-row-${ticker}`,
              'data-ticker': ticker,

            },
            cells: [
              // The select column exists only while selecting, so the add row's cells follow the header exactly.
              // The cell carries the SAME class the hidden column does (EJS writes `className: 'c-select'`): a
              // bare empty <td> has no .c-select-cell, so it stayed visible as an 86px blank column in front of
              // the date field while every other row's checkbox cell was hidden.
              { className: 'c-select c-select-cell', content: <span key="c" /> },
              <input
                key="d"
                className="t-small"
                form={`inline-tx-form-${ticker}`}
                name="tx_date"
                required
                placeholder="YYYY-MM-DD"
                value={newTx.tx_date}
                onChange={(e) => setNewTx((v) => ({ ...v, tx_date: e.target.value }))}
              />,
              <Select
                key="t"
                id={`add-tx-type-${ticker}`}
                value={newTx.type}
                onChange={(v) => setNewTx((s2) => ({ ...s2, type: v }))}
                ariaLabel="Transaction type"
                options={['buy', 'sell', 'drip', 'split'].map((t) => ({ value: t, label: t }))}
              />,
              <input
                key="q"
                className="t-small"
                form={`inline-tx-form-${ticker}`}
                placeholder="Qty"
                name="quantity"
                inputMode="decimal"
                required
                value={newTx.quantity}
                onChange={(e) => setNewTx((v) => ({ ...v, quantity: e.target.value }))}
              />,
              <PriceCell
                key="p"
                form={`inline-tx-form-${ticker}`}
                ticker={ticker}
                required
                currency={addCurrency}
                onCurrency={setAddCurrency}
                price={newTx.price}
                onPrice={(v) => setNewTx((s2) => ({ ...s2, price: v }))}
                fxRate={addFx}
                onFxRate={setAddFx}
              />,
              <input
                key="f"
                className="t-small"
                form={`inline-tx-form-${ticker}`}
                placeholder="Fees"
                name="fees"
                inputMode="decimal"
                value={newTx.fees}
                onChange={(e) => setNewTx((v) => ({ ...v, fees: e.target.value }))}
              />,
              '',
              '',
              '',
              <input
                key="n"
                className="t-small"
                form={`inline-tx-form-${ticker}`}
                placeholder="Notes"
                name="notes"
                value={newTx.notes}
                onChange={(e) => setNewTx((v) => ({ ...v, notes: e.target.value }))}
              />,
              // The action cell exactly as the EJS add row builds it (views/partials/ticker-detail.ejs): the
              // cell carries `text-align: right; white-space: nowrap;`, and the two buttons are its own
              // children — no wrapper — separated by the EJS row's `style="margin-right: 4px;"` on the ✕.
              // JSX puts no whitespace between adjacent elements, so that margin is the gap.
              {
                attrs: { style: 'text-align: right; white-space: nowrap;' },
                content: (
                  <Fragment>
                    <button
                      type="button"
                      className="ghost btn-confirm-x cancel-new-tx"
                      id={`add-tx-cancel-${ticker}`}
                      title="Cancel"
                      style={{ marginRight: 4 }}
                      onClick={() => setAddingTx(false)}
                    >
                      ✕
                    </button>
                    <button
                      type="button"
                      id={`add-tx-save-${ticker}`}
                      className="ghost btn-confirm-ok"
                      title="Add"
                      // The ✓ stays dead until every required field in the row has a value (views/list.ejs's
                      // wireRequireGate). The gap fix must not cost the gate.
                      disabled={!addRowComplete}
                      onClick={() => void saveInline()}
                    >
                      ✓
                    </button>
                  </Fragment>
                ),
              },
            ],
          },
            ]}
          />
          {/* The add row. Its inputs carry `form=<the hidden form>`, as the EJS row does, so Enter submits from
              anywhere in the row without nesting a form inside a table. */}
          <form id={`inline-tx-form-${ticker}`} className="inline-new-tx" data-ticker={ticker} onSubmit={(e) => { e.preventDefault(); void saveInline(); }} />
          <div style={{ marginTop: 4 }}>
            <a
              href="#"
              className="add-tx-link t-body"
              data-ticker={ticker}
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                // Starting a new row is a fresh action. A selection left ticking underneath keeps the totals bar
                // over the row you are typing into, and a row still open for edit would be a second form in the
                // same pane; EJS clears its current action here (cancelCurrentAction), and a selection or an
                // in-place edit is the same kind of state.
                setPicked(new Set());
                setSelecting(false);
                setEditRow(null);
                setAddingTx(true);
              }}
            >
              + Transaction
            </a>
          </div>
          <div className="pane-pagebar">
            <div className="pane-pagesize">
              <span className="muted t-small">Show</span>
              <PanePageSize value={txPageSize} onChange={setTxPageSize} noun="transactions" total={txs.length} />
            </div>
            <PanePager
              total={txs.length}
              size={pageSizeCount(txPageSize, txs.length)}
              page={txPage}
              onPage={setTxPage}
              noun="transactions"
              all={txPageSize === 'all'}
            />
          </div>
  
        </div>
      </div>

      <div
        id={`detail-pane-${ticker}-dividends`}
        data-pane="dividends"
        data-ticker={ticker}
        className={'detail-pane' + (detailsOn ? '' : ' no-details')}
        // Hidden until the payload is in. An empty-state placeholder has height, so it reads as the pane's
        // content: the tabs and the row show "no lots for this ticker" for a beat before the lots exist.
        style={pane_tab === 'dividends' && detail ? undefined : { display: 'none' }}
      >
        {divs.length === 0 ? (
          // The EJS pane collapses to this line — no tiles, no table, no pager. The SPA painted five zeroed
          // tiles and an empty table instead, which is 240px of "nothing here" the EJS page does not spend.
          <p className="muted t-body" style={{ margin: '4px 0' }}>
            No dividends recorded for this ticker.
          </p>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 12,
                margin: '6px 0 10px',
              }}
            >
              {divTiles.map(([label, value]) => (
                <div key={label} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px' }}>
                  <div className="t-tiny" style={{ color: 'var(--muted)' }}>{label}</div>
                  <div className="t-h3" style={{ fontWeight: 700, color: 'var(--text)' }}>{value}</div>
                </div>
              ))}
            </div>
            <Table
              expandable={false}
              compact
              tableClass="t-body"
              columns={[
                { label: lbl('Date') },
                { label: lbl('Dividend ($)'), num: true },
                { label: lbl('Quantity'), num: true },
                { label: lbl('Amount'), num: true },
                { label: lbl('ROC %'), num: true },
                { label: '' },
              ]}
              rows={divs.slice(0, pageSizeCount(divPageSize, divs.length)).map((d, i) => ({
                key: d.id ?? `${ticker}-div-${i}`,
                rowClass:
                  'tx-row div-row' + (openActions === `div-${d.id}` ? ' row-actions-open' : ''),
                rowAttrs: { 'data-tx-id': String(d.id ?? ''), 'data-ticker': ticker, 'data-type': 'dividend' },
                cells: [
                  d.tx_date,
                  d.perShare == null ? '—' : money(d.perShare),
                  d.shares == null ? '—' : nfmt(d.shares),
                  money(d.amount ?? 0),
                  d.roc_pct ? `${d.roc_pct}%` : '0%',
                  // The row's own control: hover the ⋮ to reveal the actions, as the EJS row does. A dividend is
                  // a transaction, so the delete is the same confirm dialog and the same endpoint.
                  {
                    className: 'row-actions-cell',
                    attrs: { style: 'text-align: right; padding: 0 10px' },
                    content: (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minHeight: 35 }}>
                        <span className="row-actions">
                          <button
                            type="button"
                            className="icon-btn row-del-btn"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => setConfirming({ id: d.id as number, type: 'dividend' } as never)}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                            </svg>
                          </button>
                        </span>
                        <button
                          type="button"
                          className="ghost lot-menu-btn"
                          style={{ fontWeight: 700 }}
                          aria-label="Row actions"
                          aria-expanded={openActions === `div-${d.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActions((prev) => (prev === `div-${d.id}` ? null : `div-${d.id}`));
                          }}
                        >
                          ⋮
                        </button>
                      </div>
                    ),
                  },
                ],
              }))}
            />
            <div className="pane-pagebar">
              <div className="pane-pagesize">
                <span className="muted t-small">Show</span>
                <PanePageSize value={divPageSize} onChange={setDivPageSize} noun="dividends" total={divs.length} />
              </div>
              <PanePager
                total={divs.length}
                size={pageSizeCount(divPageSize, divs.length)}
                page={1}
                onPage={() => undefined}
                noun="dividends"
                all={divPageSize === 'all'}
              />
            </div>
          </>
        )}
      </div>

      <div
        id={`detail-pane-${ticker}-lots`}
        data-pane="lots"
        data-ticker={ticker}
        className={'detail-pane' + (detailsOn ? '' : ' no-details')}
        // Hidden until the payload is in. An empty-state placeholder has height, so it reads as the pane's
        // content: the tabs and the row show "no lots for this ticker" for a beat before the lots exist.
        style={pane_tab === 'lots' && detail ? undefined : { display: 'none' }}
      >
        {lots.filter((l) => l.remaining > 0).length === 0 ? (
          <p className="muted t-body" style={{ margin: '4px 0' }}>No open share lots.</p>
        ) : (
          <Table
            expandable={false}
            compact
            tableClass="t-body"
            columns={[
              { label: lbl('Acquired') },
              { label: lbl('Qty'), num: true },
              { label: lbl('Remaining'), num: true },
              { label: lbl('Cost/Share'), num: true },
              { label: lbl('Cost Basis'), num: true },
              { label: lbl('Market Value'), num: true },
              { label: lbl('Unrealized'), num: true },
            ]}
            rows={lots
              .filter((l) => l.remaining > 0)
              .map((l) => {
                // The same figures ticker-detail.ejs derives per lot: basis at cost, value at today's price,
                // the difference, and the difference as a percentage of basis.
                const basis = l.remaining * l.cost_per_share;
                const val = price == null ? null : l.remaining * price;
                const unrl = val == null ? null : val - basis;
                const pct = basis > 0 && unrl != null ? (unrl / basis) * 100 : null;
                return {
                  key: l.id,
                  cells: [
                    l.acquired_date ?? '—',
                    nfmt(l.quantity),
                    nfmt(l.remaining),
                    money(l.cost_per_share),
                    money(basis),
                    val == null ? '—' : money(val),
                    // Gain and percent in one column, dollars first then the percent in parentheses — the
                    // format every other gain column uses (ticker-detail.ejs renders the same string).
                    {
                      className: unrl != null && unrl >= 0 ? 'pos' : 'neg',
                      content: unrl == null ? '—' : money(unrl) + (pct == null ? '' : ` (${pctF(pct)}%)`),
                    },
                  ],
                };
              })}
          />
        )}
      </div>

      {/* Keyed by the row being edited: the dialog's fields are seeded from `edit` in useState, which runs
          ONCE per mount. Without the key, opening Edit after the dialog had already mounted showed empty
          inputs and saving would have blanked the transaction. */}
      <AddTransaction
        key={editing ? `edit-${editing.id}` : 'edit-none'}
        listId={listId}
        open={!!editing}
        edit={editing}
        onClose={() => setEditing(null)}
      />

      <Modal
        id="confirm-modal"
        open={confirmRemove || !!confirming}
        onClose={() => {
          setConfirmRemove(false);
          setConfirming(null);
        }}
        title={
          confirming
            ? 'Delete this transaction?'
            : watch
              ? `Remove ${ticker}?`
              : `Delete ${ticker} from this list?`
        }
        subtitle={
          confirming
            ? "The list's lots and holdings are rebuilt afterwards."
            : watch
              ? 'No transactions on this ticker will be deleted.'
              : 'This action deletes all associated data related to this ticker.'
        }
        tone="danger"
        size="sm"
      >
        <p className="t-dense muted" id="confirm-modal-msg" style={{ margin: '0 0 10px' }}>
          {confirming ? `${confirming.type} ${confirming.quantity ?? ''} ${ticker} on ${confirming.tx_date}` : ''}
        </p>
        <div className="modal-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button id="confirm-modal-cancel" variant="secondary" onClick={() => { setConfirmRemove(false); setConfirming(null); }}>
            Cancel
          </Button>
          <Button
            id="confirm-modal-ok"
            variant="danger"
            onClick={async () => {
              if (confirming) {
                del.mutate(confirming.id);
                return;
              }
              // DELETE /lists/:id/tickers/:ticker is what the EJS form posts to; for a watchlist entry it
              // removes the watch row, for a holding it removes the ticker and its transactions.
              await fetch(`/api/v1/lists/${listId}/tickers/${ticker}`, {
                method: 'DELETE',
                credentials: 'same-origin',
              });
              setConfirmRemove(false);
              onChanged();
              window.location.assign(`/lists/${listId}`);
            }}
          >
            {watch ? 'Remove' : 'Delete'}
          </Button>
        </div>
      </Modal>

      <Modal
        id="confirm-remove-ticker"
        open={removingTicker}
        onClose={() => setRemovingTicker(false)}
        title={`Remove ${ticker}?`}
        subtitle="Its transactions are deleted and the list re-settled."
        tone="danger"
        size="sm"
      >
        <p className="t-body">
          {ticker} · {txs.length} transactions · {lots.length} lots
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setRemovingTicker(false)}>Keep it</Button>
          <Button
            variant="danger"
            onClick={async () => {
              await fetch(`/api/v1/lists/${listId}/tickers/${ticker}`, { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } });
              setRemovingTicker(false);
              onChanged();
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>

    </>
  );
}

export function ListPage({ id }: { id: number }) {
  const [show, setShow] = useState<'open' | 'all' | 'closed'>('open');
  const [sort, setSort] = useState<string | null>(null);
  const [asc, setAsc] = useState<boolean>(true);
  /** views/list.ejs's saveSortPref(): the mode and the direction, together, to the same route. */
  const saveSortPref = (mode: string, dir: boolean) => {
    void fetch(`/lists/${id}/sort-pref`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, asc: dir ? 'asc' : 'desc' }),
    });
  };
  const [openTicker, setOpenTicker] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deletingList, setDeletingList] = useState(false);
  const [newName, setNewName] = useState('');
  // What the open pane's selection looks like, published by the pane. The bar itself is rendered below as
  // <main>'s first child (views/list.ejs mounts it there), so this has to live with the page's other hooks —
  // above the loading/error returns, or the hook count changes between renders.
  const [selBar, setSelBar] = useState<SelBarState | null>(null);
  // The header's real height, as `--pf-hdr-h`: views/list.ejs measures it at runtime (a hard-coded 52 leaves the
  // bar 8px under the header the moment the header grows), and the selection bar's sticky offset reads it.
  useEffect(() => {
    const set = () => {
      const hdr = document.querySelector('header');
      document.documentElement.style.setProperty('--pf-hdr-h', (hdr ? hdr.offsetHeight : 52) + 'px');
    };
    set();
    window.addEventListener('resize', set);
    return () => window.removeEventListener('resize', set);
  }, []);
  const listMenu = useMenu({ menu: 'list-menu', trigger: 'list-menu-btn' });
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState<string[] | null>(null);

  const qc = useQueryClient();
  const q = useQuery<ListPayload, ApiError>({
    queryKey: ['list', id],
    queryFn: () => apiGet<ListPayload>(withFresh(`/api/v1/lists/${id}`)),
  });
  // The saved preference comes back with the list (views/list.ejs reads `list.sort_asc`), so the arrows start
  // pointing the way the table is actually sorted.
  const [ascLoaded, setAscLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || ascLoaded) return;
    if (typeof q.data.list.sort_asc === 'boolean') setAsc(q.data.list.sort_asc);
    setAscLoaded(true);
  }, [q.data, ascLoaded]);
  const pane = useQuery<{ ticker: string; detail: unknown }, ApiError>({
    queryKey: ['ticker', id, openTicker],
    queryFn: async () => {
      try {
        return await apiGet<{ ticker: string; detail: unknown }>(`/api/v1/lists/${id}/tickers/${openTicker}`);
      } catch (e) {
        // Deleting the last transaction removes the ticker from the list, and this pane's own refetch then
        // 404s. That is not an error to show: the list query is what owns the row, so hand it the news and
        // render nothing until the row goes. Keeping the previous payload (React Query's default on error)
        // left a deleted transaction on screen under a "not found" response.
        if ((e as { status?: number }).status === 404) {
          qc.invalidateQueries({ queryKey: ['list', id] });
          return { ticker: openTicker as string, detail: null };
        }
        throw e;
      }
    },
    enabled: !!openTicker,
  });

  const rows = useMemo(() => {
    const d = q.data;
    if (!d) return [];
    const mode = sort ?? d.list.sort_mode ?? 'custom';
    const byShow =
      show === 'open' ? d.holdings : show === 'all' ? [...d.holdings, ...d.closedTickers] : d.closedTickers;
    // A local order wins while reordering: the whole point is to see the new order before it is saved.
    if (reordering && order) {
      const pos = new Map(order.map((t, i) => [t, i]));
      return [...byShow].sort((a, b) => (pos.get(a.ticker) ?? 1e9) - (pos.get(b.ticker) ?? 1e9));
    }
    // `custom` means the order the server sent (the user's own ranking); the direction reverses it, exactly as
    // views/list.ejs's `customRank` comparator does. Without this branch the arrow flipped its glyph and nothing
    // else in the default sort mode.
    if (mode === 'custom') return asc ? [...byShow] : [...byShow].reverse();
    return sortRows(byShow, mode, asc);
  }, [q.data, show, sort, asc, reordering, order]);

  // Reordering: the arrows move a row one place and save, exactly as the page's arrow buttons do
  // (`persistTickerOrder()` on every click); dragging goes through the table's reorder contract.
  const saveTickerOrder = async (seq: string[]) => {
    await fetch(`/api/v1/lists/${id}/ticker-order`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ order: seq.join(',') }),
    });
    // Deliberately NOT refetching: the page's `order` state is the authority while the mode is on, so a refetch
    // that lands before the POST is committed would re-sort the table back to the old sequence and undo the drag
    // the reader just made. Done refetches once, when the mode ends.
  };
  const moveTicker = (ticker: string, dir: -1 | 1) => {
    const seq = [...(order ?? rows.map((r) => r.ticker))];
    const i = seq.indexOf(ticker);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= seq.length) return;
    [seq[i], seq[j]] = [seq[j], seq[i]];
    setOrder(seq);
    void saveTickerOrder(seq);
  };

  // Rename, shared by the dialog's Save button and its form's submit (Enter in the field).
  const saveName = async () => {
    const name = newName.trim();
    if (!name) return;
    // PATCH /lists/:id is the same route the EJS rename form posts to.
    await fetch(`/api/v1/lists/${id}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ name }),
    });
    setRenaming(false);
    qc.invalidateQueries({ queryKey: ['list', id] });
  };

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load that list: {q.error.message}</main>;

  const d = q.data!;
  const s = d.stats;
  const sign = (n: number) => (n >= 0 ? 'pos' : 'neg');

  // One quote date for the whole table: the rows are priced from the same session.
  const pricesAsOf = (() => {
    const first = rows.find((r) => r.daySessionDate || r.dayAsOf);
    const d = first && (first.daySessionDate || first.dayAsOf);
    return d ? String(d).slice(0, 10) : '';
  })();
  // The column's tooltip carries the quote date (as the EJS table does) plus, when `?fresh=1` forced a
  // refresh, what the server did — a forced refresh nobody can see is indistinguishable from none.
  const priceNote = [
    pricesAsOf ? `Prices as of ${pricesAsOf}` : '',
    q.data?.prices?.forced ? `forced ${refreshOutcome(q.data.prices)}` : '',
  ].filter(Boolean).join(' · ');
  const tableRows: Row[] = rows.map((h) => ({
    key: h.ticker,
    // The EJS rows carry these two classes and the browser suite — and the page's own CSS — address them by
    // name (`tr.holding-row`). Rendering the same row without them is what made a dozen unrelated layout
    // assertions time out: they were waiting for a row that existed under another name.
    rowClass: 'holding-row row-action',
    rowAttrs: { 'data-ticker-row': h.ticker },
    cells: [
      <div
        key="t"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          ...(h.closed ? { opacity: 0.6 } : null),
        }}
      >
        {/* The reorder affordances live in every ticker cell and are shown by the table's mode class, exactly as
            views/list.ejs writes them: the handle and the arrow pair are what the reader grabs or clicks, and
            without them "Reorder tickers" turned a mode on with nothing on screen. */}
        <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
        <span className="reorder-arrows">
          <button type="button" className="arrow-btn t-micro btn-quiet btn-xs" data-dir="up" data-ticker={h.ticker} aria-label="Move up"
            onClick={(e) => { e.stopPropagation(); moveTicker(h.ticker, -1); }}>▲</button>
          <button type="button" className="arrow-btn t-micro btn-quiet btn-xs" data-dir="down" data-ticker={h.ticker} aria-label="Move down"
            onClick={(e) => { e.stopPropagation(); moveTicker(h.ticker, 1); }}>▼</button>
        </span>
        <strong>{h.ticker}</strong>
        {h.closed ? <span className="tag">closed</span> : null}
        {h.unpriced ? <span className="tag">unpriced</span> : null}
      </div>,
      nfmt(h.shares),
      h.avg_cost == null ? '—' : money(h.avg_cost),
      h.avg_cost == null ? (
        '—'
      ) : (
        <span key="cb-t" title={money(h.avg_cost * h.shares)}>
          {abbrM(h.avg_cost * h.shares)}
        </span>
      ),
      h.price == null ? '—' : money(h.price),
      h.unpriced ? (
        <span key="v" className="muted">no price</span>
      ) : (
        <span key="v-t" title={money(h.value)}>
          {abbrM(h.value)}
        </span>
      ),
      h.dayGain == null ? (
        '—'
      ) : (
        <span
          key="d"
          className={sign(h.dayGain)}
          title={money(h.dayGain) + (h.dayGainPct != null ? ` (${abbrPct(h.dayGainPct)})` : '')}
        >
          {abbrM(h.dayGain)}
          {h.dayGainPct != null ? ` (${abbrPct(h.dayGainPct)})` : ''}
        </span>
      ),
      h.unrealized == null ? (
        '—'
      ) : (
        <span
          key="u"
          className={sign(h.unrealized)}
          title={money(h.unrealized) + (h.avg_cost && h.avg_cost > 0 ? ` (${abbrPct((h.unrealized / (h.shares * h.avg_cost)) * 100)})` : '')}
        >
          {abbrM(h.unrealized)}
          {h.avg_cost && h.avg_cost > 0 ? ` (${abbrPct((h.unrealized / (h.shares * h.avg_cost)) * 100)})` : ''}
        </span>
      ),
      h.realized != null && h.realized !== 0 ? (
        <span
          key="r"
          className={sign(h.realized)}
          title={money(h.realized) + (h.realizedPct != null ? ` (${abbrPct(h.realizedPct)})` : '')}
        >
          {abbrM(h.realized)}
          {h.realizedPct != null ? ` (${abbrPct(h.realizedPct)})` : ''}
        </span>
      ) : (
        '—'
      ),
      {
        // The caret lives in the row's own 20px cell, absolutely positioned inside it, exactly as the EJS
        // row's last cell does — the component's chevron column is 40px and not part of this table.
        attrs: { style: 'padding-left: 14px; position: relative;' },
        content: (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              paddingRight: 10,
            }}
          >
            <span className="chev" style={{ display: 'inline-flex' }}>
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
                <path
                  d="M2 4 L6 8 L10 4"
                  fill="none"
                  stroke="var(--muted)"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        ),
      },
    ],
    pane: {
      open: openTicker === h.ticker,
      render: 'lazy',
      // No .pane wrapper: the layout un-sticks first cells inside .pane, and the pane's checkbox cells are
      // absolutely positioned inside their cell — wrapped, every checkbox lands at the pane's top instead of
      // on its row's first line (a bug a screenshot cannot show, because the column only paints when selected).
      wrap: false,
      // The EJS cell's own inset (`cellAttrs: { style: 'background: rgba(0,0,0,0.15); padding: 12px 14px;' }`).
      // Without it the cell keeps the table cell's 7/0/9/0 and the pane's content runs edge to edge.
      cellStyle: { background: 'rgba(0,0,0,0.15)', padding: '12px 14px' },
      // These are the EJS hooks: the row is `.detail-row[data-detail-for=<ticker>]`, and the pane CSS
      // (.detail-row .detail-pane …) plus the browser suite both address it that way.
      rowClass: 'detail-row',
      rowAttrs: { 'data-detail-for': h.ticker },
      // The pane renders its TABS immediately and fills the body when the fetch lands. The EJS page swaps in the
      // whole fragment, so a reader there sees nothing for a moment and everything at once; here the frame
      // appears at once, which also means a click on a tab never lands on markup that is about to be replaced.
      content: (
        <TickerPane
          listId={id}
          ticker={h.ticker}
          name={(d.tickerNames || {})[h.ticker] || h.name}
          price={h.price}
          watch={h.watch}
          detail={(pane.data?.detail as Detail) ?? null}
          open={openTicker === h.ticker}
          paneSizes={d.paneSizes}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ['list', id] });
            qc.invalidateQueries({ queryKey: ['ticker', id, h.ticker] });
          }}
          onSelBarChange={setSelBar}
        />
      ),
    },
  }));

  return (
    <main>
      {/* The selection bar is the PAGE's, and it is the page's FIRST child — views/list.ejs mounts `#pf-selbar`
          as a direct child of <main>, before the list, because it is position:sticky under the app header. The
          SPA used to render it inside the expanded row's pane cell (1200px wide, 24px lower, scrolled with the
          pane) and painted a second, invented summary above the table. The pane owns the selection; the page
          owns the bar. */}
      <div id="pf-selbar" className={selBar ? 'on' : ''} role="region" aria-label="Selected transactions">
        <div className="pf-sel-caption">
          <span className="pf-sel-title" id="pf-sel-title">
            {selBar ? selBar.title : ''}
          </span>
          <span className="pf-sel-actions">
            <Button
              variant="ghost"
              size="sm"
              className="t-small"
              id="pf-sel-all"
              style={{ display: selBar && selBar.showAll ? undefined : 'none' }}
              onClick={() => selBar?.onSelectAll()}
            >
              Select all {selBar ? selBar.total : ''}
            </Button>
            <Button variant="ghost" size="sm" className="t-small" id="pf-sel-clear" onClick={() => selBar?.onClear()}>
              Clear
            </Button>
          </span>
        </div>
        <Table
          expandable={false}
          compact
          tableId="pf-sel-table"
          bodyId="pf-sel-body"
          tableClass=""
          columns={[
            { label: lbl('Date') },
            { label: lbl('Type') },
            { label: lbl('Qty'), num: true },
            { label: 'Avg price', num: true },
            { label: lbl('Fees'), num: true },
            { label: lbl('Value'), num: true },
            { label: lbl('Unrealized'), num: true },
            { label: lbl('Realized'), num: true },
          ]}
          rows={selBar && selBar.cells.length ? [{ key: 'totals', rowAttrs: { id: 'pf-sel-row' }, cells: selBar.cells }] : []}
        />
      </div>
      <div id="list-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* The EJS page's heading IS the list switcher (a select that reads "Holdings ▾"), with the list's
            type as a tag beside it — not an h1 plus a second control saying the same thing. The two groups
            are EJS's: a name row on the left, one trailing span on the right. The span is INLINE, so its
            buttons wrap among themselves at 390px (the EJS behaviour) instead of the menu dropping to a line
            of its own — which is what a wrapping flex row with a spacer did. */}
        <div id="list-name-row" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Select
            value={String(d.list.id)}
            options={d.userLists.map((l) => ({ value: String(l.id), label: l.name }))}
            onChange={(v) => window.location.assign(`/lists/${v}`)}
            ariaLabel="Switch list"
            fontSize={22}
            fontWeight={700}
          />
          <span className="tag">{d.list.type.replace('_', ' ')}</span>
        </div>
        <span style={{ position: 'relative' }}>
        {/* Reordering is a mode with its own two buttons in the row; everything else lives in the ⋯ menu,
            which is where the EJS page keeps it — the header carries one primary action and one menu. */}
        {reordering ? (
          <>
            <Button
              id="tickers-reorder-cancel"
              className="btn-confirm-x"
              title="Cancel reordering"
              ariaLabel="Cancel reordering"
              onClick={() => { setReordering(false); setOrder(null); }}
            >
              ✕
            </Button>
            {' '}
            <Button
              id="tickers-reorder-done"
              className="btn-confirm-ok"
              title="Done reordering"
              ariaLabel="Done reordering"
              onClick={async () => {
                const seq = (order ?? rows.map((r) => r.ticker)).join(',');
                await fetch(`/api/v1/lists/${id}/ticker-order`, {
                  method: 'POST',
                  credentials: 'same-origin',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ order: seq }),
                });
                setReordering(false);
                setOrder(null);
                qc.invalidateQueries({ queryKey: ['list', id] });
              }}
            >
              ✓
            </Button>
            {' '}
          </>
        ) : (
          <>
            <AddTicker listId={id} onAdded={() => qc.invalidateQueries({ queryKey: ['list', id] })} />
            {' '}
          </>
        )}
        {/* While tickers are being reordered the ⋯ is out of reach: the mode has its own ✕/✓ pair, and the menu is
            where "Reorder tickers" lives — the page hides it for the same reason (`listMenuBtnEl.style.display`). */}
        {/* A bare `<button class="ghost">`, as views/list.ejs writes it: the shared Button adds its own `btn`
            class, which is a different class list (and a different box) from the page's control. The button is
            the element itself — no wrapper span, because the page has none and the wrapper was the one extra
            empty span the parity scan counted. `position: relative` is the anchor the menu positions against. */}
        <button
            {...listMenu.triggerProps}
            type="button"
            className="ghost"
            ref={listMenu.triggerRef}
            style={{ position: 'relative', display: reordering ? 'none' : 'inline-flex' }}
            aria-label="List actions"
          >
            …
          </button>
          <Menu
            {...listMenu.menuProps}
            align="right"
            // The EJS menu's own `min-width: 210px` and its four `menu-row` buttons (14px, 8px 14px padding,
            // 35px tall). This panel had been built out of the ACCOUNT menu's rows — `.um-item`, 13px in a
            // 31px row, in a 180px box — so the same dropdown was a different component's look.
            minWidth={210}
          >
            <Button
              shape="menu-row"
              attrs={{ role: 'menuitem', 'data-menu-item': '' }}
              className="t-dense"
              id="edit-list-open"
              icon={
                <span className="menu-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </span>
              }
              onClick={() => {
                listMenu.setOpen(false);
                setNewName(d.list.name);
                setRenaming(true);
              }}
            >
              Edit list
            </Button>
            {rows.length > 0 ? (
              <>
                <Button
                  shape="menu-row"
                  attrs={{ role: 'menuitem', 'data-menu-item': '' }}
                  className="t-dense"
                  id="reorder-tickers-toggle"
                  icon={
                    <span className="menu-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M8 4v16M8 20l-3-3M8 20l3-3" />
                        <path d="M16 20V4M16 4l-3 3M16 4l3 3" />
                      </svg>
                    </span>
                  }
                  onClick={() => {
                    listMenu.setOpen(false);
                    setOrder(rows.map((r) => r.ticker));
                    // Reordering is a mode of the TABLE: every expanded pane collapses with it, as the page does
                    // (`document.querySelectorAll('.detail-row').forEach(d => d.style.display = 'none')`), and a
                    // pane's add row, selection or editor goes with the pane it belongs to.
                    setOpenTicker(null);
                    setReordering(true);
                  }}
                >
                  Reorder tickers
                </Button>
              </>
            ) : null}
            <Button
              shape="menu-row"
              attrs={{ role: 'menuitem', 'data-menu-item': '' }}
              className="js-import-csv-open t-dense"
              id="import-csv-open"
              icon={
                <span className="menu-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <path d="M7 10l5-5 5 5" />
                    <path d="M12 5v12" />
                  </svg>
                </span>
              }
              onClick={() => {
                listMenu.setOpen(false);
                setImporting(true);
              }}
            >
              Import CSV
            </Button>
            <Button
              shape="menu-row"
              attrs={{ role: 'menuitem', 'data-menu-item': '' }}
              variant="danger"
              className="t-dense"
              id="delete-list-open"
              icon={
                <span className="menu-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                  </svg>
                </span>
              }
              onClick={() => {
                listMenu.setOpen(false);
                setDeletingList(true);
              }}
            >
              Delete list
            </Button>
          </Menu>
        </span>
      </div>

      {/* The EJS page's own margin for this row (`style: 'margin-top: 12px;'`): the default 16 is the
          dashboard's, and 4px here pushed every row of the holdings table down with it. */}
      <Cards style={{ marginTop: 12 }}>
        <Card label={lbl('Total value')} value={<span title={money(s.totalValue)}>{abbrM(s.totalValue)}</span>} />
        <Card label={lbl('Cost basis')} value={<span title={money(s.totalCost)}>{abbrM(s.totalCost)}</span>} />
        <Card
          label={lbl('Unrealized')}
          valueClass={sign(s.unrealized)}
          // views/list.ejs wraps the abbreviation in a titled span and writes the percent beside it as text:
          // "<span title=\"$…\">$1.01M</span> (17.50%)".
          value={
            <>
              <span title={money(s.unrealized)}>{abbrM(s.unrealized)}</span>
              {s.unrealizedPct != null ? ` (${pctF(s.unrealizedPct)}%)` : ''}
            </>
          }
        />
        <Card
          label={lbl('Day change')}
          valueClass={sign(s.dayGain)}
          value={
            <>
              <span title={money(s.dayGain)}>{abbrM(s.dayGain)}</span>
              {s.dayChangePct != null ? ` (${pctF(s.dayChangePct)}%)` : ''}
            </>
          }
        />
        <Card
          label={lbl('Total gain')}
          // EJS colours this figure by sign, like Unrealized and Day change: on this row the one neutral
          // value would read as "not a gain" beside two coloured ones.
          valueClass={sign(s.totalGain)}
          value={<span title={money(s.totalGain)}>{abbrM(s.totalGain)}</span>}
          // The tip belongs on the card whose figures it explains: the list's own total, broken into its three
          // parts as views/partials/tips/list-total-gain.ejs writes them — unrealized, realized AND dividends.
          // The dividends line was missing, so the card explained $5.35M with figures that only added to $5.35M
          // once the missing $401K was known to be there.
          tip={
            // `nowrap` is the page's own rule on this bubble (views/partials/tips/list-total-gain.ejs sets
            // `white-space: nowrap`): three figures on three lines, sized to the longest. Without it the bubble
            // wrapped into a 103px column, four lines deep, where the page draws one 218px line per figure.
            <InfoTip anchor="card" nowrap>
              <>
                {' '}Unrealized: {money(s.unrealized)}
                {s.unrealizedPct != null ? ` (${s.unrealizedPct.toFixed(2)}%)` : ''}
                <br />
                Realized: {money(s.realized)}
                <br />
                Dividends: {money(s.dividends)}
              </>
            </InfoTip>
          }
        />
      </Cards>

      <section className="panel-card" style={{ marginTop: 0, paddingTop: 16 }}>
        {/* The page's own instruction line, shown only in reorder mode. */}
        <div className="t-body" id="tickers-reorder-hint" style={{ display: reordering ? undefined : 'none', margin: '8px 0', color: 'var(--muted)' }}>
          Drag rows by the ⋮⋮ handle or use the arrows to reorder, then choose … → Done.
        </div>
        {/* The EJS filter row is `margin: 10px 0` in a card whose padding-top is 16 — the numbers matter
            because the table's first row sits directly under it and every row is measured against the other
            front end's. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', margin: '10px 0' }}>
          <Select
            id="show-filter"
            value={show}
            fontSize={13}
            onChange={(v) => setShow(v as typeof show)}
            ariaLabel="Show"
            // views/list.ejs's order, which is All · Open · Closed with Open selected — not Open first.
            options={[
              { value: 'all', label: 'Show: All' },
              { value: 'open', label: 'Show: Open' },
              { value: 'closed', label: 'Show: Closed' },
            ]}
          />
          {/* The sort control is a segmented unit with its direction toggle, as the page's is: `.seg` holds the
              picker and the ▲/▼ button, and the picker's menu is sized to its own options, not the viewport. */}
          <span className="seg" style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, position: 'relative' }}>
            <Select
              id="sort-filter"
              value={String(sort ?? d.list.sort_mode ?? 'custom')}
              fontSize={13}
              onChange={(v) => {
                setSort(v as typeof sort);
                saveSortPref(v, asc);
              }}
              ariaLabel="Sort"
              options={SORT_OPTIONS.map(([v, text]) => ({ value: v, label: text }))}
            />
            <button
              type="button"
              id="sort-direction"
              className="seg-dir t-small"
              aria-label="Toggle sort order"
              title={asc ? 'Ascending' : 'Descending'}
              onClick={() => {
                const next = !asc;
                setAsc(next);
                saveSortPref(String(sort ?? d.list.sort_mode ?? 'custom'), next);
              }}
              style={{ border: 'none', minHeight: 0, borderLeft: '1px solid var(--border)', background: 'var(--panel)', color: 'var(--accent)', padding: '0 10px', cursor: 'pointer' }}
            >
              {asc ? '▲' : '▼'}
            </button>
          </span>
        </div>
        {rows.length === 0 ? (
          <p className="muted t-body">No holdings yet — add a ticker above.</p>
        ) : (
        // views/list.ejs wraps the table in a `.table-scroll` of its own, on top of the one the component
        // renders — the page's DOM has two nested scroll boxes, and the scanner counts one more.
        <div className="table-scroll">
        <Table
          // No id: views/list.ejs gives its holdings table none, and the suites address it as
          // `table.holdings-table:not(#import-preview)`. The id here was an invention the parity scan counted.

          // The EJS table's class is the hook the stylesheet and the suite use (`table.holdings-table`); the id
          // alone is not enough, and a table found by neither is a table that reads as missing.
          // Class ORDER is part of the contract the scanner compares (views/list.ejs: "holdings-table reorder-off
          // row-hover").
          tableClass={'holdings-table ' + (reordering ? 'reorder-on' : 'reorder-off') + ' row-hover'}
          chevron={false}
          columns={[
            { label: 'Ticker' },
            { label: lbl('Shares'), num: true },
            { label: lbl('AC/share'), num: true },
            { label: lbl('Cost basis'), num: true },
            { label: lbl('Price'), num: true },
            { label: lbl('Market Value'), num: true },
            {
              // The day's move is only meaningful with the day it belongs to, and the column is where a
              // reader looks for it. The date comes from the first row's price session, as the EJS table's
              // does — one quote date for the whole table.
              label: (
                <span
                  title={priceNote}
                >
                  {/* views/list.ejs writes this header as 'Day Change' while the card above it says
                      'Day change' — the two spellings are the page's own, so the SPA keeps both. */}
                  {lbl('Day Change')}
                </span>
              ),
              num: true,
            },
            { label: lbl('Unrealized'), num: true },
            { label: lbl('Realized'), num: true },
            // The EJS table's own caret column: 20px wide, the caret absolutely positioned inside the cell
            // (padding-left 14) rather than the component's 40px chevron column. `chevron={false}` plus this
            // column is what keeps the table's last 16px of width identical to the EJS page's.
            { label: '', width: 20 },
          ]}
          rows={tableRows}
          // While reordering, a row click is a grab, not an expand: the two modes are exclusive, and a pane
          // opening mid-drag would pull the row out from under the pointer.
          onClickRow={(key) => {
            if (reordering) return;
            setOpenTicker((t) => (t === key ? null : key));
          }}
          reorder={
            reordering
              ? {
                  enabled: true,
                  // The preview is the table's; a drop says what it means: the new sequence, saved at once (the
                  // page's own dragover handler calls persistTickerOrder() on drop too).
                  onDrop: (from, to, seqFromTable) => {
                    // The table hands over the sequence it previewed; adopt it verbatim so the saved order is the
                    // order the reader saw, not a replay of the move.
                    const seq = seqFromTable ?? [...(order ?? rows.map((r) => r.ticker))];
                    if (!seqFromTable) {
                      if (from < 0 || to < 0 || from >= seq.length || to >= seq.length) return;
                      const [moved] = seq.splice(from, 1);
                      seq.splice(to, 0, moved);
                    }
                    setOrder(seq);
                    void saveTickerOrder(seq);
                  },
                }
              : undefined
          }
          empty="No holdings yet."
        />
        </div>
        )}
      </section>

      {/* No page-level add-transaction dialog: EJS adds a transaction from the ticker pane's own
          `.add-tx-link` row (the SPA has that too), and the ⋯ menu never carried one. */}

      <Modal id="rename-list-modal" open={renaming} onClose={() => setRenaming(false)} title="Rename this list" size="sm">
        {/* The EJS page's rename form is `#edit-list-form` with `input[name=name]`; the dialog keeps both so the
            form is the same form, not a lookalike with different hooks. */}
        <form
          id="edit-list-form"
          onSubmit={(e) => {
            e.preventDefault();
            void saveName();
          }}
        >
          <Field label="Name" htmlFor="list-name-input" required>
            <input
              id="list-name-input"
              name="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Button variant="secondary" onClick={() => setRenaming(false)}>Cancel</Button>
          <Button type="submit" disabled={!newName.trim() || newName.trim() === d.list.name}>
            Save name
          </Button>
        </div>
        </form>
      </Modal>

      <Modal
        id="delete-list-modal"
        open={deletingList}
        onClose={() => setDeletingList(false)}
        title="Delete this list?"
        subtitle="Its transactions go with it. This cannot be undone."
        tone="danger"
        size="sm"
      >
        <p className="t-body">
          <strong>{d.list.name}</strong> — {d.holdings.length} holdings, {d.stats.totalValue ? money(d.stats.totalValue) : '$0.00'}.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setDeletingList(false)}>Keep it</Button>
          <Button
            id="delete-list-confirm"
            variant="danger"
            onClick={async () => {
              await fetch(`/api/v1/lists/${id}`, { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } });
              window.location.assign('/');
            }}
          >
            Delete list
          </Button>
        </div>
      </Modal>
      <ImportDialog
        listId={id}
        open={importing}
        onClose={() => setImporting(false)}
        onImported={() => qc.invalidateQueries({ queryKey: ['list', id] })}
      />
    </main>
  );
}
